import type { TrustedKey, VerifyResult } from '@slate/plugin-api';
import type { InstalledPlugin } from '../runtime';
import { verifySignature } from './verifySignature';

export interface PluginVerifier {
  verify(plugin: InstalledPlugin): Promise<VerifyResult>;
}

/** Bridges an installed plugin to verifySignature. In-process/first-party
 *  plugins (a module is supplied) are pre-trusted and skip signing (spec §4.1);
 *  sealed plugins MUST carry a signature and pass. */
export function createPluginVerifier(opts: {
  trustedKeys: TrustedKey[];
  readFile(pluginId: string, path: string): Promise<Uint8Array>;
}): PluginVerifier {
  return {
    verify: async (plugin) => {
      if (plugin.module) return { ok: true };
      if (!plugin.signature) return { ok: false, reason: 'sealed plugin has no signature' };
      return verifySignature({
        manifest: plugin.manifest,
        signature: plugin.signature,
        trustedKeys: opts.trustedKeys,
        readFile: (p) => opts.readFile(plugin.manifest.id, p),
      });
    },
  };
}

/** Fail-closed default: admit in-process modules, refuse any sealed plugin
 *  because no key set / reader is configured. Hosts inject a real verifier. */
export const defaultVerifier: PluginVerifier = {
  verify: async (plugin) =>
    plugin.module
      ? { ok: true }
      : { ok: false, reason: 'no signature verifier configured for a sealed plugin' },
};
